/**
 * Copyright (c) 2023, CYBRA Corp. All rights reserved.
 */
import { Component, Input, OnInit } from "@angular/core";
import { FormControl } from "@angular/forms";
import { AntennaInstance } from "app/models/antenna-instance";
import { DriverObjectInstance } from "app/models/driver-object-instance";
import { EmGroup } from "app/models/em-group";
import { Image } from "app/models/image";
import { InstanceData } from "app/models/instance-data";
import { LocationInstance } from "app/models/location-instance";
import { ObjectInstance } from "app/models/object-instance";
import { Property } from "app/models/property";
import { PropertyType } from "app/models/property-type";
import { ReaderInstance } from "app/models/reader-instance";
import { ServerObjectInstance } from "app/models/server-object-instance";
import { ServiceInstance } from "app/models/service-instance";
import { TagInstance } from "app/models/tag-instance";
import { User } from "app/models/user";
import { ZoneInstance } from "app/models/zone-instance";
import { AntennaService } from "app/services/antenna.service";
import { DriverObjectService } from "app/services/driver-object.service";
import { GroupService } from "app/services/group.service";
import { ImageService } from "app/services/image.service";
import { LocationService } from "app/services/location.service";
import { ObjectService } from "app/services/object.service";
import { ReaderService } from "app/services/reader.service";
import { ServerObjectService } from "app/services/server-object.service";
import { ServiceService } from "app/services/service.service";
import { TagService } from "app/services/tag.service";
import { UserService } from "app/services/user.service";
import { ZoneService } from "app/services/zone.service";
import { TypeaheadMatch } from "ngx-bootstrap/typeahead";
import { from, of } from "rxjs";

/**
 * 
 * This Component is used to model a given "Property" and an array of "InstanceData" 
 * of the data for the Property.  This is used in the "EntityInstanceDialogComponent" 
 * to show the "Property Data" for the Instance being created / edited.  This is an 
 * improvement on the old "InstanceDataInfrastructureComponent" as that was not 
 * designed with "Multi-value"  Property Support, this can handle it by having
 * the "InstanceData" be an array. 
 * 
 * TODO: This page / component will eventually have problems modeling Types
 * that have a lot of possible values as there is no paging currently being done.
 * 
 * Example: If the Property Type is set to "TAG_REFERENCE" and there are 1,000,000
 * Tags in the system, all the Tags will attempt to be loaded as the availableTags.
 * 
 * UPDATE 08/13/2025: The above mentioned issue has been taken care using Filter & 
 * Paging the requests made to the backend. Giving user a Auto-complete option 
 * when he starts typing beyond first 3 letters.
 * 
 */
@Component({
    selector: 'app-property-data-infrastructure',
    templateUrl: './property-data-infrastructure.component.html'
  })
  export class PropertyDataInfrastructureComponent implements OnInit {
    
    /** Store a reference to the enum in the component so that it can be used in the html file [ngSwitch] */ 
    propertyType = PropertyType;
    
    /** Property that is being modeled. All the "data" InstanceData[] records are for this Property. */
    @Input()
    property: Property

    @Input()
    isCreate: boolean = false;

    /** 
     * Array of InstanceData that is defined for the "property" Property being modeled. These are the individual values
     * the User has entered for the Property.  In the case of a "Multi-value" property, multiple records are needed / allowed.
     */
    @Input()
    data: InstanceData[];


    //Variables for "Type-Ahead" logic when setting values
    typeaheadValue: string;
    typeaheadLoading: boolean;
    typeaheadNoResults: boolean;
    searchControl = new FormControl();
    pageSize : number  = 20;
    start : number = 0;
    order : string = 'ASC';

    /**
     * Constructor
     */
    constructor(
        private antennaService: AntennaService,
        private driverObjectService: DriverObjectService,
        private groupService: GroupService,
        private imageService: ImageService,
        private locationService: LocationService,
        private objectService: ObjectService,
        private readerService: ReaderService,
        private serverObjectService: ServerObjectService,
        private serviceService: ServiceService,
        private tagService: TagService,
        private userService: UserService,
        private zoneService: ZoneService
    ) { } 

    ngOnInit(): void {}

    onInputChange(value: string, datum: InstanceData) {
        datum.isEditing = true;
        if (!value || value.length < 2) {
            datum.instanceResults$ = of([]);
            return;
    }

    switch (this.property.type) {
        case PropertyType.ANTENNA_REFERENCE:
            datum.instanceResults$ = from(this.fetchAntennaInstancesByName(value));
            break;
        case PropertyType.DRIVEROBJECT_REFERENCE:
            datum.instanceResults$ = from(this.fetchDriverObjectsInstancesByName(value));
            break;
        case PropertyType.GROUP_REFERENCE:
            datum.instanceResults$ = from(this.fetchGroupsByName(value));
            break;
        case PropertyType.IMAGE_REFERENCE:
            datum.instanceResults$ = from(this.fetchImagesByName(value));
            break;
        case PropertyType.LOCATION_REFERENCE:
            datum.instanceResults$ = from(this.fetchLocationsInstancesByName(value));
            break;
        case PropertyType.OBJECT_REFERENCE:
            datum.instanceResults$ = from(this.fetchObjectInstancesByName(value));
            break;
        case PropertyType.READER_REFERENCE:
            datum.instanceResults$ = from(this.fetchReadersInstancesByName(value));
            console.log("In Reader Reference Case");
            break;
        case PropertyType.SERVEROBJECT_REFERENCE:
            datum.instanceResults$ = from(this.fetchServerObjectsInstancesByName(value));
            break;
        case PropertyType.SERVICE_REFERENCE:
            datum.instanceResults$ = from(this.fetchServicesInstancesByName(value));
            break;
        case PropertyType.TAG_REFERENCE:
            datum.instanceResults$ = from(this.fetchTagsInstancesByName(value));
            break;
        case PropertyType.USER_REFERENCE:
            datum.instanceResults$ = from(this.fetchUsersByName(value));
            break;
        case PropertyType.ZONE_REFERENCE:
            datum.instanceResults$ = from(this.fetchZonesInstancesByName(value));
            break;
		case PropertyType.OBJECTTYPE_REFERENCE:
			datum.instanceResults$ = from(this.fetchObjectTypesByName(value));
			break;	
        default:
        }
    }

    onInstanceSelected(event: TypeaheadMatch, datum: InstanceData) {
        datum.object_value = event.item;
        datum.value = event.item.id;
        datum.isEditing = false;
    }

    private async fetchObjectInstancesByName(term: string): Promise<ObjectInstance[]> {
        return this.objectService.getObjectInstancesFiltered(this.start, this.pageSize, this.order, term);
    }

    private async fetchAntennaInstancesByName(term: string): Promise<AntennaInstance[]> {
        return this.antennaService.getAntennaInstancesFiltered(this.start, this.pageSize, this.order, term);
    }

    private async fetchDriverObjectsInstancesByName(term: string): Promise<DriverObjectInstance[]> {
        return this.driverObjectService.getDriverObjectInstancesFiltered(this.start, this.pageSize, this.order, term);
    }

    private async fetchGroupsByName(term: string): Promise<EmGroup[]> {
        return this.groupService.getGroupsFiltered(this.start, this.pageSize, this.order, term);
    }

    private async fetchImagesByName(term: string): Promise<Image[]> {
        return this.imageService.getImagesFiltered(this.start, this.pageSize, this.order, term);
    }

    private async fetchReadersInstancesByName(term: string): Promise<ReaderInstance[]> {
        return this.readerService.getReaderInstancesFiltered(this.start, this.pageSize, this.order, term);
    }

    private async fetchServerObjectsInstancesByName(term: string): Promise<ServerObjectInstance[]> {
        return this.serverObjectService.getServerObjectInstancesFiltered(this.start, this.pageSize, this.order, term);
    }

    private async fetchLocationsInstancesByName(term: string): Promise<LocationInstance[]> {
        return this.locationService.getLocationInstancesFiltered(this.start, this.pageSize, this.order, term);
    }

    private async fetchServicesInstancesByName(term: string): Promise<ServiceInstance[]> {
        return this.serviceService.getServiceInstancesFiltered(this.start, this.pageSize, this.order, term);
    }

    private async fetchTagsInstancesByName(term: string): Promise<TagInstance[]> {
        return this.tagService.getTagInstancesFiltered(this.start, this.pageSize, this.order, term);
    }
    
    private async fetchUsersByName(term: string): Promise<User[]> {
        return this.userService.getUsersFiltered(this.start, this.pageSize, this.order, term);
    }

    private async fetchZonesInstancesByName(term: string): Promise<ZoneInstance[]> {
        return this.zoneService.getZoneInstancesFiltered(this.start, this.pageSize, this.order, term);
    }
	
    private async fetchObjectTypesByName(term: string): Promise<any[]> {
        return this.objectService.getObjectTypesForReference(
            this.start,
            this.pageSize,
            this.order,
            term
        );
	}

    /**
     * Used for "Multi-value" Property Support.
     * 
     * If the Property being modeled is marked as allowing Multiple values, a "+" button is displayed
     * to allow the User to add new Data records.  This method gets called when the User clicks on that
     * button.
     * 
     * This creates a new InstanceData, assigns it's property to the one being modeled and adds it to the
     * InstanceData[] being displayed.
     */
    public addNewValue(): void {
        const newData = new InstanceData();
        newData.property = this.property;
        newData.object_value = null;
        this.data.push(newData);
    }

    /**
     * Used for "Multi-value" Property Support.
     * 
     * If the Property being modeled is marked as allowing Multiple values, a "-" button is displayed
     * to allow the User to add remove Data records.  This method gets called when the User clicks on 
     * that button.
     * 
     * This removes the InstanceData at the given index from the InstanceData[] being displayed.
     */
    public removeValueAtIndex(i: number): void {
        this.data.splice(i, 1);
    }

    /**
     * Called when a "STRING" Type Data value is changed.
     * UI Models the Instance Data's "value" property... Need to update the "object_value" property to
     * the new value.
     */
    public onStringChanged(instanceData: InstanceData): void {
        instanceData.object_value = instanceData.value;
    }

    /**
     * Called when a "BOOLEAN" Type Data value is changed.
     * UI Models the Instance Data's "object_value" property... Need to update the "value" property to
     * the new value.
     */
    public onBooleanChanged(instanceData: InstanceData): void {
        instanceData.value = instanceData.object_value == true? "true" : "false";
    }

    /**
     * Called when a "DOUBLE" Type Data value is changed.
     * UI Models the Instance Data's "object_value" property... Need to update the "value" property to
     * the new value.
     */
    public onDoubleChanged(instanceData: InstanceData): void {
        instanceData.value = instanceData.object_value == null? null : instanceData.object_value.toString();
    }


    /**
     * Returns the URL path to use for Image Data being represented
     * by the given InstanceData
     */
    public getImagePath(instanceData: InstanceData): string {
        if (instanceData?.object_value?.id != null) {
            return this.imageService.getImageSourceForImageId(instanceData.object_value.id);
        } else {
            return 'assets/images/no_photo2.png';
        }
    }

  }
